/**
 * The facts of the topic picked in the side menu. Overview: a card per main sh:PropertyGroup with
 * its lead rows; a topic: its rows in cards by dview:sections; All facts: the plain table. Triples
 * no shape covers follow as "RINF" (era: terms) and "Other properties".
 */

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, StatusBadge } from '@bn-apps/dim-ui-react';
import { Description, Info } from '@bn-apps/design-system-icons';
import { resultLevel, type ValidationResult } from '@hooks/useValidationResults';
import { shortenUri } from '@hooks/useEntity';
import type { EntityViewModel } from '@hooks/useEntityView';
import {
  DVIEW,
  DVIEW_NS,
  leadingPredicate,
  pickLang,
  type ViewPropertyShape,
} from '@utils/viewShapes.utils';
import type { EntityObject, EntityProperty } from '@/types';
import {
  checkState,
  ERA,
  isKeyInfoRow,
  isTitleRow,
  prettyPath,
  worstLevel,
  type CheckState,
} from '../page';
import { localName, words } from '../linearLocation';
import { chooseViewer, widgetFor } from '../widgets/registry';
import { aggregate } from '../widgets/figures';
import { AnyValue, recordName, uriText } from '../widgets/values';
import { RinfValues } from '../widgets/rinf';
import { Chip, chipIcon } from '../widgets/chip';
import { domainOf } from '../domains';
import { OrderedMasonry } from '../masonry';
import { contentsKinds } from '../widgets/area';
import { sectionMap, sectionsFor } from '../widgets/sections';
import type { Lens, WidgetContext } from '../widgets/types';
import styles from '../EntityTemplate.module.scss';

const PREVIEW = 5;
/** Rows a card shows before "Show all", when its group sets no dview:rowLimit. */
const ROW_LIMIT = 4;
// A few well-picked rows per topic on the overview; the topic has the rest
const OVERVIEW_ROWS = 3;

const DATA = 'https://data.banenor.no/data/';
const RDF_TYPE = 'http://www.w3.org/1999/02/22-rdf-syntax-ns#type';
const ERA_LISTS = [`${ERA}notApplicable`, `${ERA}notYetAvailable`];

export interface FactRow {
  id: string;
  label: string;
  property: ViewPropertyShape | null;
  /** The triple's predicate, for rows no shape describes. */
  predicate: string | null;
  values: EntityObject[];
  viewer: string | null;
  results: ValidationResult[];
  check: CheckState;
  /** An era: triple shown in a domain group, away from the RINF group. */
  fromRinf?: boolean;
}

export type FactsView = 'grouped' | 'plain';

export interface FactGroup {
  id: string;
  /** The sh:PropertyGroup's IRI; none for the groups of triples no shape covers. */
  iri?: string;
  title: string;
  order: number;
  rows: FactRow[];
  /** dview:rowLimit: rows the card shows before "Show all". */
  rowLimit?: number;
  /** dview:from of a dview:GroupPictureViewer: the rows the card's picture can show, first with a value wins. */
  picture?: string[];
  /** dview:domain: the colour and icon of the group's card. */
  domain?: string;
  /** Straight from the triples, without a shape: long, so folded after a few rows. */
  raw?: boolean;
  /** dview:intro: one line under the title on the group's topic page. */
  intro?: string;
  /** dview:lanes: the hero lanes the group's topic shows. */
  lanes?: string[];
  /** dview:kinds: the object kinds (symbols) the hero keeps lit for the group's topic. */
  kinds?: string[];
}

/** The group's name in the side menu and the ?topic= link: "electrification", not "viewgroup-electrification". */
export const topicOf = (group: FactGroup) => group.id.replace(/^viewgroup-/, '');

// A predicate as a plain name: era:hasLevelCrossings reads "Level crossings"
const sentence = (name: string) => {
  const text = words(name).toLowerCase().replace(/^has /, '');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/** sh:name, else the local name of the path's first predicate (hasGeometry). */
function rowName(property: ViewPropertyShape, language: string): string {
  const name = pickLang(property.name, language);
  if (name) return name;
  const predicate = leadingPredicate(property.path);
  return predicate ? sentence(localName(predicate)) : property.id;
}

function cardinality(property: ViewPropertyShape): string {
  return `${property.minCount ?? 0}..${property.maxCount === null ? '*' : property.maxCount}`;
}

const valueText = (values: EntityObject[], context: WidgetContext) =>
  values
    .map((value) =>
      value.type === 'uri'
        ? (context.resolved[value.value]?.[0] ?? shortenUri(value.value, context.prefixes))
        : value.value
    )
    .join(', ');

/** A row's RINF parameter (dview:rinf): the first one the entity has, as a shared row can name several. */
function rinfParameter(
  property: ViewPropertyShape,
  rinf: Map<string, EntityObject[]>
): string | undefined {
  const parameters = property.settings.rinf ?? [];
  return parameters.find((parameter) => rinf.has(parameter)) ?? parameters[0];
}

/** The RINF value a row is compared with (dview:rinf), from the entity's own era: triples. */
function rinfCell(
  row: FactRow,
  context: WidgetContext,
  rinf: Map<string, EntityObject[]>,
  t: (key: string, options?: Record<string, unknown>) => string
): { tag: string; text: string; tone: string } {
  if (row.predicate?.startsWith(ERA))
    return {
      tag: `era:${localName(row.predicate)}`,
      text: t('entity.template.rinf.onlyRinf'),
      tone: 'neutral',
    };
  const parameter = row.property ? rinfParameter(row.property, rinf) : undefined;
  if (!parameter) return { tag: '', text: '–', tone: 'muted' };
  const theirs = rinf.get(parameter) ?? [];
  const tag = `era:${localName(parameter)}`;
  if (theirs.length === 0)
    return { tag, text: t('entity.template.rinf.missing'), tone: 'critical' };
  const ours = row.property ? aggregate(row.property, row.values) : null;
  const theirsNumber = Number(theirs[0].value);
  if (ours !== null && Number.isFinite(theirsNumber)) {
    return Math.abs(ours - theirsNumber) < 0.05
      ? { tag, text: t('entity.template.rinf.same', { value: theirsNumber.toLocaleString(context.language) }), tone: 'success' }
      : {
          tag,
          text: t('entity.template.rinf.differs', { value: theirsNumber.toLocaleString(context.language) }),
          tone: 'warning',
        };
  }
  const theirsText = valueText(theirs, context);
  return row.property && theirsText.toLowerCase() === valueText(row.values, context).toLowerCase()
    ? { tag, text: t('entity.template.rinf.same', { value: theirsText.slice(0, 80) }), tone: 'success' }
    : { tag, text: theirsText.slice(0, 80), tone: 'neutral' };
}

function LensCell({
  row,
  context,
  rinf,
}: {
  row: FactRow;
  context: WidgetContext;
  rinf: Map<string, EntityObject[]>;
}): JSX.Element | null {
  const { t } = useTranslation();
  const { property, results } = row;
  const cell = (tag: string, text: string, tone = 'neutral') => (
    <span className={styles.lensCell} data-tone={tone}>
      {tag && <span className={styles.lensTag}>{tag}</span>}
      {text && <span>{text}</span>}
    </span>
  );
  switch (context.lens) {
    case 'default':
      return null;
    case 'shape': {
      if (!property) return cell('', t('entity.template.facts.noShape'), 'muted');
      const range =
        property.datatypes.map((datatype) => shortenUri(datatype, context.prefixes)).join(' | ') ||
        (property.classIri ? shortenUri(property.classIri, context.prefixes) : '');
      return cell(
        cardinality(property),
        [prettyPath(property, context.prefixes), range].filter(Boolean).join(' · ')
      );
    }
    case 'quality': {
      if (!property) return cell('–', t('entity.template.check.notChecked'), 'muted');
      const tone = { pass: 'success', warn: 'warning', gap: 'critical', notChecked: 'muted' }[
        row.check
      ];
      // The design system's status badge: an icon and the word, never colour alone
      const status = ({ pass: 'success', warn: 'warning', gap: 'critical', notChecked: 'neutral' } as const)[row.check];
      return (
        <span className={styles.lensCell} data-tone={tone}>
          <StatusBadge status={status}>{t(`entity.template.check.${row.check}`)}</StatusBadge>
          {results.map((result, index) => (
            <span key={index}>
              {resultLevel(result) === 'error' ? '✕ ' : ''}
              {result.message}
              {result.test && <em className={styles.testData}> {t('entity.template.testData')}</em>}
            </span>
          ))}
        </span>
      );
    }
    case 'rinf': {
      const { tag, text, tone } = rinfCell(row, context, rinf, t);
      return cell(tag, text, tone);
    }
    default:
      return cell('', '–', 'muted');
  }
}

const COLUMN_HEAD: Partial<Record<Lens, string>> = {
  shape: 'entity.template.facts.column.shape',
  quality: 'entity.template.facts.column.quality',
  lineage: 'entity.template.facts.column.lineage',
  rinf: 'entity.template.facts.column.rinf',
  time: 'entity.template.facts.column.time',
};

interface FactsProps {
  groups: FactGroup[];
  context: WidgetContext;
  view: FactsView;
  /** The side menu's choice: "overview", "all" (the table) or a group's topic. */
  topic: string;
  onTopic: (topic: string) => void;
  /** "Find a fact", typed in the side menu. */
  query: string;
  onQuery: (query: string) => void;
  rinf: Map<string, EntityObject[]>;
  /** The lens switch: it changes the facts, so it sits in their header. */
  lensSwitch: ReactNode;
}

/** The row a group card draws as its picture: the first of dview:from that has a value. */
function pictureRow(group: FactGroup): FactRow | null {
  for (const key of group.picture ?? []) {
    const row = group.rows.find((candidate) => candidate.property?.key === key && candidate.values.length > 0 && candidate.viewer);
    if (row) return row;
  }
  return null;
}

/** A group's domain icon in its domain colour, before the group's title. */
export function DomainMark({ domain, raw }: { domain?: string; raw?: boolean }): JSX.Element {
  // Without a domain (Details, and RINF until it joins the domains): a neutral tile
  const found = domainOf(domain);
  const Icon = found?.icon ?? (raw ? Description : Info);
  return (
    <span
      className={styles.domainMark}
      style={{ '--domain': found?.color ?? 'var(--dim-ink-secondary)' } as CSSProperties}
      aria-hidden="true"
    >
      <Icon width={16} height={16} />
    </span>
  );
}

const Facts = ({
  groups: allGroups,
  context,
  view,
  topic,
  onTopic,
  query,
  onQuery: setQuery,
  rinf,
  lensSwitch,
}: FactsProps): JSX.Element | null => {
  const { t } = useTranslation();
  const pictures = view !== 'plain';
  // Triples without a shape row: in the plain table only
  const groups = pictures ? allGroups.filter((group) => group.id !== 'other') : allGroups;
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [allValues, setAllValues] = useState<Set<string>>(new Set());
  const needle = query.trim().toLowerCase();
  // "Find a fact" opens the topic that has a match: the one picked if it has one, else the first
  useEffect(() => {
    if (!needle || topic === 'all') return;
    const hits = groups.filter((group) =>
      group.rows.some((row) => [row.label, group.title, valueText(row.values, context)].some((text) => text.toLowerCase().includes(needle)))
    );
    if (hits.length > 0 && !hits.some((group) => topicOf(group) === topic)) onTopic(topicOf(hits[0]));
    // Only a new search moves the menu
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needle]);
  if (groups.length === 0) return null;
  // What a widget shows beyond the row's values (the objects inside a tunnel)
  const shownText = (row: FactRow) =>
    row.property && row.viewer ? (widgetFor(row.viewer).text?.({ property: row.property, values: row.values, context }) ?? '') : '';
  // "Find a fact": rows whose name, group or value text holds the words; groups left empty drop out
  const shownGroups = needle
    ? groups
        .map((group) => ({
          ...group,
          rows: group.rows.filter((row) =>
            [row.label, group.title, valueText(row.values, context), shownText(row)].some((text) => text.toLowerCase().includes(needle))
          ),
        }))
        .filter((group) => group.rows.length > 0)
    : groups;
  const columnHead = context.lens ? COLUMN_HEAD[context.lens] : undefined;

  const moreToggle = (row: FactRow) =>
    row.values.length > 6 ? (
      <Button
        variant="tertiary"
        size="xs"
        aria-expanded={allValues.has(row.id)}
        aria-label={
          allValues.has(row.id)
            ? undefined
            : t('entity.template.facts.moreValues', { count: row.values.length - 6 })
        }
        onClick={() => {
          const next = new Set(allValues);
          if (next.has(row.id)) next.delete(row.id);
          else next.add(row.id);
          setAllValues(next);
        }}
      >
        {allValues.has(row.id) ? t('entity.template.facts.showFewer') : `+${row.values.length - 6}`}
      </Button>
    ) : null;

  const renderValue = (row: FactRow, rowPictures = pictures) => {
    const rowContext = { ...context, pictures: rowPictures, compact: true };
    if (row.values.length === 0 && context.lens === 'quality') {
      return <span className={styles.missingValue}>{t('entity.template.facts.noValue')}</span>;
    }
    if (row.predicate?.startsWith(ERA)) {
      return (
        <span className={styles.rawValues}>
          <RinfValues
            predicate={row.predicate}
            values={row.values}
            context={rowContext}
            limit={allValues.has(row.id) ? 200 : 6}
          />
          {moreToggle(row)}
        </span>
      );
    }
    // Links to other records read as the pills of "Connected to"; dview: widgets draw their own.
    const links = row.values.every(
      (value) =>
        value.type === 'uri' &&
        value.value.startsWith(DATA) &&
        !value.value.includes('_timeinstant_') &&
        context.resolved[value.value]
    );
    // A nested node whose shape asks for a widget (dview:) draws through it instead of as a pill
    const nestedWidget = row.property
      ? context.model?.nestedShapes[row.property.id]?.properties.some((part) =>
          part.viewer?.startsWith(DVIEW_NS)
        )
      : false;
    if (
      rowPictures &&
      links &&
      row.values.length > 0 &&
      !row.viewer?.startsWith(DVIEW_NS) &&
      !nestedWidget
    ) {
      return (
        <span className={styles.chips}>
          {/* The same place under several IRIs (Hamar three times) shows once, by its name */}
          {[...new Map(row.values.map((value) => [uriText(value.value, context), value])).values()]
            .slice(0, allValues.has(row.id) ? undefined : 12)
            .map((value) => (
              <Chip
                key={value.value}
                iri={value.value}
                icon={chipIcon(row.property, row.predicate)}
                label={recordName(
                  value.value,
                  context,
                  row.property?.classIri
                    ? (context.resolved[row.property.classIri]?.[0] ??
                        localName(row.property.classIri))
                    : null
                )}
                context={context}
              />
            ))}
        </span>
      );
    }
    if (!row.property || !row.viewer) {
      return (
        <span className={styles.rawValues}>
          {(allValues.has(row.id) ? row.values : row.values.slice(0, 6)).map((value) => (
            <AnyValue key={value.value} value={value} context={context} />
          ))}
          {moreToggle(row)}
        </span>
      );
    }
    return widgetFor(row.viewer).render({
      property: row.property,
      values: row.values,
      context: { ...rowContext, nestedShape: context.model?.nestedShapes[row.property.id] ?? null },
    });
  };

  // In a card the lens line shows only what needs a look: a failed rule, or RINF on a row it pairs with
  const lensInCard = (row: FactRow) =>
    context.lens === 'quality'
      ? Boolean(row.property)
      : context.lens === 'rinf'
        ? Boolean(row.fromRinf || (row.property && row.property.settings.rinf?.length))
        : true;

  const factRow = (row: FactRow, rowPictures = pictures, picture?: ReactNode, inCard = false) => {
    const level = worstLevel(row.results);
    const wide = row.viewer ? widgetFor(row.viewer).wide : false;
    const caption = row.property?.caption
      ? pickLang(row.property.caption, context.language)
      : undefined;
    return (
      <div
        key={row.id}
        className={`${styles.factRow} ${wide || picture ? styles.factRowWide : ''}`}
      >
        <dt
          title={
            row.property
              ? (pickLang(row.property.description, context.language) ?? undefined)
              : (row.predicate ?? undefined)
          }
        >
          {row.label}
        </dt>
        <dd className={styles.factValue}>
          {picture ?? renderValue(row, rowPictures)}
          {caption && <span className={styles.factCaption}>{caption}</span>}
          {context.lens === 'default' && level && (
            <span
              className={styles.qualityDot}
              data-level={level}
              title={row.results.map((result) => result.message).join('\n')}
            />
          )}
        </dd>
        <dd className={styles.factLens}>
          {(!inCard || lensInCard(row)) && <LensCell row={row} context={context} rinf={rinf} />}
        </dd>
      </div>
    );
  };

  const showAllButton = (group: FactGroup, expanded: boolean) =>
    group.raw && group.rows.length > PREVIEW ? (
      <Button
        variant="tertiary"
        size="sm"
        className={styles.showAll}
        onClick={() => {
          const next = new Set(open);
          if (expanded) next.delete(group.id);
          else next.add(group.id);
          setOpen(next);
        }}
      >
        {expanded
          ? t('entity.template.facts.showFewer')
          : t('entity.template.facts.showAll', { count: group.rows.length, group: group.title })}
      </Button>
    ) : null;

  const isLead = (row: FactRow) => row.property?.settings?.lead?.[0] === 'true';
  // The picture's row first, then the lead rows, then the rest
  const orderedRows = (group: FactGroup) => {
    const lead = pictureRow(group);
    return {
      lead,
      rows: [
        ...(lead ? [lead] : []),
        ...group.rows.filter((row) => row !== lead && isLead(row)),
        ...group.rows.filter((row) => row !== lead && !isLead(row)),
      ],
    };
  };
  const pictureOf = (lead: FactRow | null) =>
    lead?.property && lead.viewer
      ? widgetFor(lead.viewer).render({
          property: lead.property,
          values: lead.values,
          context: { ...context, pictures: true, compact: false, nestedShape: context.model?.nestedShapes[lead.property.id] ?? null },
        })
      : null;
  const overview = topic === 'overview' && !needle;
  // Overview: the main topics only; the reference topics (identification, technical, details, RINF) are in the menu
  const reference = (group: FactGroup) => group.raw || !group.domain || group.domain === 'identification' || group.domain === 'technical';
  const cardGroups = overview ? shownGroups.filter((group) => !reference(group)) : shownGroups;
  // Two columns of cards, one on a phone
  const cards = (
    <OrderedMasonry className={styles.factCards} minColumnWidth={352} maxColumns={2}>
      {cardGroups.map((group) => {
        const limit = group.rowLimit ?? ROW_LIMIT;
        const { lead, rows: ordered } = orderedRows(group);
        // Overview: the lead rows of each topic, the topic has the rest; a search shows every row it found
        const leads = ordered.filter((row) => row === lead || isLead(row));
        const expanded = open.has(group.id) || Boolean(needle);
        const shown = overview
          ? (leads.length > 0 ? leads : ordered).slice(0, Math.min(limit, OVERVIEW_ROWS))
          : expanded
            ? ordered
            : ordered.slice(0, limit);
        const picture = pictureOf(lead);
        // The contents of a track, tunnel or bridge get the full width, unless only a few kinds lie inside
        const fewKinds = group.rows.every(
          (row) =>
            row.viewer === DVIEW.ContentsViewer &&
            row.property !== null &&
            contentsKinds(context, row.property, t('entity.template.spot.untyped')) <= 3
        );
        // Overview: the contents as a short summary in a normal card, the topic has the tiles
        const compactContents = overview && group.domain === 'contents';
        const wide =
          (!compactContents && group.domain === 'contents' && !fewKinds) ||
          (!compactContents &&
            group.rows.some(
              (row) => row.viewer && row.viewer !== DVIEW.ContentsViewer && widgetFor(row.viewer).wide
            ));
        return (
          <section
            key={group.id}
            id={`facts-${group.id}`}
            className={styles.factCard}
            data-wide={wide || undefined}
            tabIndex={-1}
            aria-labelledby={`facts-title-${group.id}`}
          >
            <h3 id={`facts-title-${group.id}`} className={styles.factCardTitle}>
              <DomainMark domain={group.domain} raw={group.raw} />
              <span className={styles.factCardName}>{group.title}</span>
              <span className={styles.tocCount}>{group.rows.length}</span>
            </h3>
            <dl className={styles.factRows}>
              {shown.map((row) =>
                factRow(
                  row,
                  true,
                  compactContents && row.property && row.viewer
                    ? widgetFor(row.viewer).render({ property: row.property, values: row.values, context: { ...context, summary: true } })
                    : row === lead && picture ? (
                    <div className={styles.factCardPicture}>{picture}</div>
                  ) : undefined,
                  true
                )
              )}
            </dl>
            {overview && (ordered.length > shown.length || compactContents) && (
              <Button variant="tertiary" size="sm" className={styles.showAll} onClick={() => onTopic(topicOf(group))}>
                {t('entity.template.topics.open', { count: group.rows.length, group: group.title })}
              </Button>
            )}
            {!overview && ordered.length > limit && !needle && (
              <Button
                variant="tertiary"
                size="sm"
                className={styles.showAll}
                aria-expanded={expanded}
                onClick={() => {
                  const next = new Set(open);
                  if (expanded) next.delete(group.id);
                  else next.add(group.id);
                  setOpen(next);
                }}
              >
                {expanded
                  ? t('entity.template.facts.showFewer')
                  : t('entity.template.facts.showAll', {
                      count: group.rows.length,
                      group: group.title,
                    })}
              </Button>
            )}
          </section>
        );
      })}
    </OrderedMasonry>
  );

  // One topic: its title, a line on what it holds, then all its rows in two columns (a search: the rows it found)
  const topicGroup = (needle ? shownGroups : groups).find((group) => topicOf(group) === topic);
  const otherHits = needle ? shownGroups.filter((group) => group !== topicGroup) : [];
  const topicPage = topicGroup
    ? (() => {
        const { lead, rows } = orderedRows(topicGroup);
        const picture = pictureOf(lead);
        // The group's dview:sections splits its rows into cards: by the shape's local name, or the era: property of a RINF fact
        const sectionOf = sectionMap(context, topicGroup.iri);
        const sectionKey = (row: FactRow) =>
          (row.property ? sectionOf.get(localName(row.property.id)) : row.predicate ? sectionOf.get(row.predicate) : undefined) ?? '';
        const sections = sectionsFor(context, rows.map(sectionKey), topicGroup.title);
        const contentsRow =
          topicGroup.domain === 'contents' && rows.length === 1 && !needle && (rows[0].viewer === DVIEW.FacilitiesViewer || rows[0].viewer === DVIEW.ContentsViewer)
            ? rows[0]
            : null;
        return (
          <div id={`facts-${topicGroup.id}`} className={styles.topicPage}>
            {topicGroup.intro && !needle && <p className={styles.topicIntro}>{topicGroup.intro}</p>}
            {otherHits.length > 0 && (
              <p className={styles.topicIntro}>
                {t('entity.template.topics.alsoFound')}{' '}
                {otherHits.map((group) => (
                  <Button key={group.id} variant="tertiary" size="xs" onClick={() => onTopic(topicOf(group))}>
                    {group.title} ({group.rows.length})
                  </Button>
                ))}
              </p>
            )}
            <OrderedMasonry className={styles.factCards} minColumnWidth={352} maxColumns={2}>
              {/* The objects on a track or in a structure: a card per section of their tiles */}
              {contentsRow?.property && contentsRow.viewer
                ? widgetFor(contentsRow.viewer).render({
                    property: contentsRow.property,
                    values: contentsRow.values,
                    context: { ...context, pictures: true, compact: false, sectionCard: { card: styles.factCard, title: styles.factCardTitle } },
                  })
                : sections.map((section) => {
                const sectionRows = rows.filter((row) => sectionKey(row) === section.key);
                const wide = sectionRows.some((row) => row.viewer && widgetFor(row.viewer).wide);
                return (
                  <section key={section.key} className={styles.factCard} data-wide={wide || undefined} aria-label={section.title}>
                    {sections.length > 1 && <h3 className={styles.factCardTitle}>{section.title}</h3>}
                    <dl className={styles.factRows}>
                      {sectionRows.map((row) =>
                        factRow(row, true, row === lead && picture ? <div className={styles.factCardPicture}>{picture}</div> : undefined, true)
                      )}
                    </dl>
                  </section>
                );
              })}
            </OrderedMasonry>
          </div>
        );
      })()
    : null;

  const table = (
    <div className={styles.facts}>
      <div className={styles.factsGroups}>
        {shownGroups.map((group) => {
          const expanded = open.has(group.id) || !group.raw;
          const shown = expanded ? group.rows : group.rows.slice(0, PREVIEW);
          return (
            <section
              key={group.id}
              id={`facts-${group.id}`}
              className={styles.factsGroup}
              aria-labelledby={`facts-title-${group.id}`}
            >
              <div className={styles.groupHead}>
                <h3 id={`facts-title-${group.id}`} className={styles.groupTitle}>
                  <DomainMark domain={group.domain} raw={group.raw} />
                  {group.title}
                </h3>
                <span />
                <span className={styles.columnHead}>{columnHead ? t(columnHead) : ''}</span>
              </div>
              <dl className={styles.factRows}>{shown.map((row) => factRow(row))}</dl>
              {showAllButton(group, expanded)}
            </section>
          );
        })}
      </div>
    </div>
  );

  return (
    <section className={styles.section} aria-labelledby="entity-facts">
      {/* The title of what the side menu picked, with the lens on its right */}
      <div className={styles.sectionHead}>
        <h2 id="entity-facts" className={styles.topicHeading}>
          {topicGroup && <DomainMark domain={topicGroup.domain} raw={topicGroup.raw} />}
          {topicGroup ? topicGroup.title : t(`entity.template.topics.${topic === 'all' ? 'all' : 'overview'}`)}
        </h2>
        {lensSwitch}
      </div>
      {/* Keyed by the view, so a switch fades the new layout in */}
      <div key={`${topic} ${view}`} className={styles.factsView}>
        {shownGroups.length === 0 ? (
          <p className={styles.noMatch}>
            {t('entity.template.facts.noMatch', { query: query.trim() })}{' '}
            <Button variant="tertiary" size="sm" onClick={() => setQuery('')}>
              {t('entity.template.facts.clear')}
            </Button>
          </p>
        ) : topic === 'all' ? (
          table
        ) : topicPage ? (
          topicPage
        ) : (
          cards
        )}
      </div>
    </section>
  );
};

export function factGroups(
  model: EntityViewModel,
  context: WidgetContext,
  results: Map<string, ValidationResult[]>,
  entityProperties: EntityProperty[],
  titles: { other: string; rinf: string; details: string },
  includeEmpty: boolean
): FactGroup[] {
  const byGroup = new Map<string, FactGroup>();
  const groupOf = (groupId: string): FactGroup => {
    let group = byGroup.get(groupId);
    if (!group) {
      const definition = model.shapes?.groups.get(groupId);
      group = {
        id: (groupId.split(/[#/]/).pop() ?? 'details').toLowerCase() || 'details',
        iri: groupId || undefined,
        title: definition
          ? (pickLang(definition.label, context.language) ?? groupId)
          : titles.details,
        order: definition ? definition.order : 900,
        rows: [],
        rowLimit: Number(definition?.settings.rowLimit?.[0]) || undefined,
        domain: definition?.settings.domain?.[0],
        intro: (definition?.intro && pickLang(definition.intro, context.language)) || undefined,
        lanes: (definition?.settings.lanes ?? []).flatMap((value) => value.split(/\s+/)).filter(Boolean),
        kinds: (definition?.settings.kinds ?? []).flatMap((value) => value.split(/\s+/)).filter(Boolean),
        picture:
          definition?.viewer === DVIEW.GroupPictureViewer
            ? (definition.settings.from ?? []).flatMap((value) => value.split(/\s+/)).filter(Boolean)
            : undefined,
      };
      byGroup.set(groupId, group);
    }
    return group;
  };
  for (const property of model.properties) {
    // Key figures are facts too when their shape gives them a group; labels make the title.
    if (isTitleRow(property) || (isKeyInfoRow(property) && !property.groupId)) continue;
    if (property.viewer && widgetFor(property.viewer).hero) continue;
    const values = model.values[property.id] ?? [];
    const rowResults = results.get(property.id) ?? [];
    const viewer = chooseViewer(property, values);
    const hasContent =
      widgetFor(viewer).render({ property, values, context: { ...context, compact: true } }) !==
      null;
    if (!hasContent && !(includeEmpty && ((property.minCount ?? 0) > 0 || rowResults.length > 0)))
      continue;
    const groupId =
      property.groupId && model.shapes?.groups.has(property.groupId) ? property.groupId : '';
    groupOf(groupId).rows.push({
      id: property.id,
      label: rowName(property, context.language),
      property,
      predicate: null,
      values,
      viewer,
      results: rowResults,
      check: checkState(property, values, rowResults),
    });
  }
  // Triples no shape covers: an era: term a row already compares with (dview:rinf) shows as that row's
  // RINF mark; the other era: terms go to the group of their RINF section, or else the RINF group.
  const raw = (keep: (predicate: string) => boolean): FactRow[] =>
    entityProperties
      .filter(
        (property) =>
          property.predicate !== RDF_TYPE &&
          !model.coveredPredicates.has(property.predicate) &&
          keep(property.predicate)
      )
      .map((property) => ({
        id: property.predicate,
        label: ERA_LISTS.includes(property.predicate)
          ? `${sentence(localName(property.predicate))} (${property.objects.length})`
          : sentence(localName(property.predicate)),
        property: null,
        predicate: property.predicate,
        values: property.objects,
        viewer: null,
        results: [],
        check: 'notChecked' as const,
      }));
  const paired = new Set(
    Array.from(byGroup.values()).flatMap((group) =>
      group.rows.flatMap((row) => (row.property ? (row.property.settings.rinf ?? []) : []))
    )
  );
  const sections = Array.from(model.shapes?.groups.values() ?? []).flatMap((definition) =>
    (definition.settings.rinfSection ?? [])
      .flatMap((value) => value.split(/\s+/))
      .filter(Boolean)
      .map((prefix) => ({ prefix, groupId: definition.id }))
  );
  // A group whose dview:sections names the era: property takes it; else the group of its RINF section
  const named = new Map<string, string>(
    Array.from(model.shapes?.groups.values() ?? []).flatMap((definition) =>
      (definition.settings.sections ?? [])
        .map((entry) => entry.split('=')[0])
        .filter((key) => key.startsWith('era:'))
        .map((key) => [`${ERA}${key.slice(4)}`, definition.id] as const)
    )
  );
  const sectionOf = (predicate: string): string | undefined => {
    if (named.has(predicate)) return named.get(predicate);
    const index = model.shapes?.rinfIndex?.get(predicate);
    if (!index) return undefined;
    return sections
      .filter(({ prefix }) => index === prefix || index.startsWith(`${prefix}.`))
      .sort((a, b) => b.prefix.length - a.prefix.length)[0]?.groupId;
  };
  const rinfRows: FactRow[] = [];
  for (const row of raw((predicate) => predicate.startsWith(ERA) && !paired.has(predicate))) {
    const groupId = sectionOf(row.id);
    if (groupId) groupOf(groupId).rows.push({ ...row, fromRinf: true });
    else rinfRows.push(row);
  }
  const otherRows = raw((predicate) => !predicate.startsWith(ERA));
  // RINF-only rows go after the rows of the shapes
  for (const group of byGroup.values())
    group.rows.sort(
      (a, b) =>
        (a.property?.order ?? Number.MAX_SAFE_INTEGER) -
        (b.property?.order ?? Number.MAX_SAFE_INTEGER)
    );
  const groups = Array.from(byGroup.values());
  if (rinfRows.length > 0)
    groups.push({ id: 'rinf', title: titles.rinf, order: 950, rows: rinfRows, raw: true });
  if (otherRows.length > 0)
    groups.push({ id: 'other', title: titles.other, order: 990, rows: otherRows, raw: true });
  return groups.sort((a, b) => a.order - b.order);
}

export default Facts;
